import type { ReactNode } from 'react';

export interface HeaderProps {
  /** アプリ名（ロゴ横に表示） */
  appName: string;
  /** ロゴスロット。各アプリがロゴ要素（必要ならリンクで包む）を渡す */
  logo?: ReactNode;
  /** モバイルメニュー開閉トグル */
  onMenuToggle?: () => void;
  /** ヘッダー右側のアクションスロット（テーマトグル・言語切替など） */
  actions?: ReactNode;
  /** ユーザーメニュースロット（アバター + ドロップダウンなど） */
  userMenu?: ReactNode;
}

/**
 * @graph-stack web-design-system
 * @graph-domain Engineering
 * @graph-business 全幅 sticky ヘッダー。ロゴ・アプリ名・モバイルメニュートグル・右側アクションスロット・ユーザーメニュースロットを配置する。i18n / ルーティングに依存せず、可変部は全てスロットで注入する
 * @graph-connects none
 */
export function Header({ appName, logo, onMenuToggle, actions, userMenu }: HeaderProps): ReactNode {
  return (
    <header className="sticky top-0 z-[var(--z-sticky)] bg-[var(--color-bg-surface)]/80 backdrop-blur-xl border-b border-[var(--color-border)]">
      <div className="flex items-center justify-between h-14 px-4 md:px-6">
        {/* Left: Menu button (mobile) + Logo + App name */}
        <div className="flex items-center gap-3">
          {onMenuToggle && (
            <button
              onClick={onMenuToggle}
              className="md:hidden p-2 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors"
              aria-label="メニューを開く"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          )}
          <div className="flex items-center gap-2">
            {logo}
            <span className="hidden sm:block font-semibold text-[var(--color-text-primary)]">
              {appName}
            </span>
          </div>
        </div>

        {/* Right: Actions + User menu */}
        <div className="flex items-center gap-2">
          {actions}
          {userMenu}
        </div>
      </div>
    </header>
  );
}
